import { CustomerBillingModel, CustomerModel } from 'api-hooks/customer';
import { Option } from 'baseui/select';
import { UserStatusEnum } from 'common/constants/enum';
import { toTitleCase, transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import useDialog from 'hooks/use-dialog';
import CustomerSelectInput from 'legacy-modules/master/customer/components/select-input';
import EmployeeSelectInput from 'legacy-modules/master/select-input/employee-select-input';
import CustomerBillingSelectInput from 'legacy-modules/sales/component/customer-billing-select-input';
import CustomerSalesWarningToast from 'legacy-modules/sales/component/customer-sales-warning-toast';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import NoticeSuspendDialog from 'legacy-modules/sales/notice-suspend-dialog';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { DocumentSaleFormType } from './form-type';

interface Props {}

function transformer(item: CustomerBillingModel): Option {
  return {
    id: item.id,
    label: transformLabel([item.name, item.address, item.phone]),
  };
}

export default function SalesDetailField(props: Props) {
  const { t } = useTranslation();
  const { control, setValue, getValues } =
    useFormContext<DocumentSaleFormType>();
  const { editable } = useFormState();
  const [prevCustomerId, setPrevCustomerId] = React.useState<
    string | undefined
  >(undefined);

  const dialog = useDialog();

  const [customerId, total, remainingTransactionQuota] = useWatch({
    control,
    name: ['customerId', 'total', 'remainingTransactionQuota'],
  });

  const showLimitTransactionDialog = React.useCallback(() => {
    dialog.showCustom({
      render: (close) => <NoticeSuspendDialog onClose={close} type="limited" />,
    });
  }, [dialog]);

  const isExceedingQuota =
    editable && remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    if (isExceedingQuota) {
      showLimitTransactionDialog();
    }
    // @ts-ignore
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, showLimitTransactionDialog, isExceedingQuota]);

  React.useEffect(() => {
    if (!customerId || customerId === '') {
      setValue('customerId', '');
      setValue('customerBillingOptions', []);
      setValue('customerBillingId', '');
      setValue('address', '');
      setValue('phone', '');
      setValue('hasDueReceivable', false);
      setValue('customerStatus', '');
      setValue('remainingTransactionQuota', null);
      // @ts-ignore
      setValue('isExceedingQuota', false);
    }
  }, [customerId, setValue]);

  const onChangeCustomer = React.useCallback(
    async (item: CustomerModel) => {
      if (item) {
        const _options =
          (item?.billings || []).map((item) => transformer(item)) || [];
        setValue('customerBillingOptions', _options);
        setValue('phone', item.phone || '');
        setValue('address', toTitleCase(item.address || ''));

        const remainingTransactionQuota = item.system.remainingTransactionQuota;
        const isExceedingQuota =
          item.system.isTransactionLimited && total > remainingTransactionQuota;
        setValue(
          'remainingTransactionQuota',
          remainingTransactionQuota || null,
        );
        // @ts-ignore
        setValue('isExceedingQuota', isExceedingQuota);

        if (editable) {
          setValue(
            'customerBillingId',
            _options.length ? (_options?.[0]?.id as string) || '' : '',
          );

          if (prevCustomerId !== item.id) {
            setPrevCustomerId(item.id);
            if (prevCustomerId || !getValues('travelDocument')) {
              if (item.status === UserStatusEnum.UnderSurveillance) {
                dialog.showCustom({
                  render: (close) => (
                    <NoticeSuspendDialog onClose={close} type="suspend" />
                  ),
                });
              }
              if (item.hasDueReceivables) {
                dialog.showCustom({
                  render: (close) => (
                    <NoticeSuspendDialog onClose={close} type="receivable" />
                  ),
                });
              }
              if (isExceedingQuota) showLimitTransactionDialog();
            }

            setValue('hasDueReceivable', item.hasDueReceivables);
            setValue('customerStatus', item.status);
          }
        }
      } else {
        setValue('customerBillingOptions', []);
      }
    },
    [
      dialog,
      editable,
      getValues,
      prevCustomerId,
      setValue,
      showLimitTransactionDialog,
      total,
    ],
  );

  return (
    <CardSection>
      <Text variant="HeadingXSmall">{t('common:sales_details')}</Text>
      <SeparatorVertical $margin={24} />
      <FullContainer>
        <SectionContainer>
          <HalfContainer
            $style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            <TransactionAt
              name="transactionAt"
              clearable={false}
              hasTime
              required
            />
          </HalfContainer>
          {getValues('travelDocument') && (
            <HalfContainer>
              <Input
                type="text"
                name="number"
                label={t('common:invoice_number')}
                placeholder={t('common:invoice_number')}
                disabled
              />
            </HalfContainer>
          )}
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <SectionContainer>
              <HalfContainer>
                <EmployeeSelectInput
                  name="adminId"
                  label={t('invoice:operator')}
                  placeholder={t('invoice:operator')}
                  required
                  disabled
                />
              </HalfContainer>
              <HalfContainer>
                <EmployeeSelectInput
                  name="operatorId"
                  label={t('invoice:sales')}
                  placeholder={t('invoice:sales')}
                  required
                />
              </HalfContainer>
            </SectionContainer>
          </HalfContainer>
          <HalfContainer>
            <SectionContainer>
              <HalfContainer>
                <EmployeeSelectInput
                  name="salesmanId"
                  label={t('invoice:lead')}
                  placeholder={t('invoice:lead')}
                />
              </HalfContainer>
              <HalfContainer>
                <EmployeeSelectInput
                  name="documentAdminId"
                  label={t('invoice:admin_document')}
                  placeholder={t('invoice:admin_document')}
                />
              </HalfContainer>
            </SectionContainer>
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <CustomerSelectInput
              name="customerId"
              required
              onAfterDetailChange={onChangeCustomer}
              isShowTransactionQuota
            />
          </HalfContainer>
          <HalfContainer>
            <CustomerBillingSelectInput name="customerBillingId" />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="text"
              name="phone"
              label={t('common:phone_number')}
              placeholder={t('common:phone_number')}
              disabled
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="text"
              name="address"
              label={t('common:address')}
              placeholder={t('common:address')}
              disabled
              fontCase="TitleCase"
            />
          </HalfContainer>
        </SectionContainer>
      </FullContainer>
      <CustomerSalesWarningToast />
    </CardSection>
  );
}
